import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getWorkers, deleteWorker } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Eye, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getExpiryStatusClass } from '@/lib/utils';
import SmartSearch from '@/components/SmartSearch';

// Column definitions for smart search
const searchColumns = [
  { key: 'full_name', label: 'Name', type: 'text' },
  { key: 'client_name', label: 'Client', type: 'text' },
  { key: 'nationality', label: 'Nationality', type: 'text' },
  { key: 'passport_number', label: 'Passport', type: 'text' },
  { key: 'phone', label: 'Phone', type: 'text' },
  { key: 'emirates_id', label: 'Emirates ID', type: 'text' },
  { key: 'visa_status', label: 'Visa Status', type: 'select', options: [
    { value: 'Valid', label: 'Valid' },
    { value: 'Expiring Soon', label: 'Expiring Soon' },
    { value: 'Expired', label: 'Expired' },
    { value: 'New', label: 'New' },
  ]},
  { key: 'passport_expiry', label: 'Passport Expiry', type: 'date' },
  { key: 'visa_expiry', label: 'Visa Expiry', type: 'date' },
  { key: 'emirates_id_expiry', label: 'EID Expiry', type: 'date' },
];

// Quick filter presets
const quickFilters = [
  { label: 'Valid Visa', filters: { visa_status: 'Valid' } },
  { label: 'Expiring Soon', filters: { visa_status: 'Expiring Soon' } },
  { label: 'Expired', filters: { visa_status: 'Expired' } },
];

export default function WorkersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  
  const [allWorkers, setAllWorkers] = useState([]);
  const [filteredWorkers, setFilteredWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [workerToDelete, setWorkerToDelete] = useState(null);

  const loadWorkers = useCallback(async () => {
    try {
      const params = { limit: 1000 };
      if (clientId) params.client_id = clientId;
      
      const res = await getWorkers(params);
      setAllWorkers(res.data.items);
      setFilteredWorkers(res.data.items);
    } catch (error) {
      toast.error('Failed to load workers');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadWorkers();
  }, [loadWorkers]);

  const handleDelete = async () => {
    if (!workerToDelete) return;
    try {
      await deleteWorker(workerToDelete.id);
      toast.success('Worker deleted');
      loadWorkers();
    } catch (error) {
      toast.error('Failed to delete worker');
    } finally {
      setDeleteDialogOpen(false);
      setWorkerToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Workers</h1>
          <p className="text-muted-foreground">Manage worker profiles and documents</p>
        </div>
        <Button onClick={() => navigate('/workers/new')} data-testid="new-worker-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Worker
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <SmartSearch
            data={allWorkers}
            columns={searchColumns}
            onFilter={setFilteredWorkers}
            quickFilters={quickFilters}
            placeholder="Search by name, passport, nationality, Emirates ID, phone..."
          />
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Nationality</TableHead>
                  <TableHead>Passport</TableHead>
                  <TableHead>Emirates ID</TableHead>
                  <TableHead>Visa Expiry</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkers.map((worker) => (
                  <TableRow key={worker.id} data-testid={`worker-row-${worker.id}`}>
                    <TableCell>
                      <Link to={`/workers/${worker.id}`} className="font-medium hover:text-primary">
                        {worker.full_name}
                      </Link>
                    </TableCell>
                    <TableCell>{worker.client_name}</TableCell>
                    <TableCell>{worker.nationality}</TableCell>
                    <TableCell className="font-mono text-sm">{worker.passport_number}</TableCell>
                    <TableCell className="font-mono text-sm">{worker.emirates_id || '-'}</TableCell>
                    <TableCell>
                      {worker.visa_expiry ? (
                        <Badge className={getExpiryStatusClass(worker.visa_expiry)}>
                          {formatDate(worker.visa_expiry)}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={worker.visa_status === 'Valid' ? 'default' : worker.visa_status === 'Expiring Soon' ? 'warning' : 'destructive'}>
                        {worker.visa_status || 'New'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/workers/${worker.id}`)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/workers/${worker.id}/edit`)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { setWorkerToDelete(worker); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredWorkers.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No workers found matching your search criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="mt-4 text-sm text-muted-foreground">
            Showing {filteredWorkers.length} of {allWorkers.length} workers
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Worker</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{workerToDelete?.full_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setWorkerToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
